import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ChatMessage } from '../../_config/interfaces/interfaces';
import { useChatHistoryApi } from '../../store/api/hooks/chatHistoryApiHook';

interface Props {
  sessionId: string | null;
  onSessionCreated: (id: string) => void;
  onSessionsChanged: () => void;
}

const SUGGESTED_PROMPTS = [
  {
    text: 'Which areas need the most improvement?',
    iconPath: 'M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.732 0 2.813-1.875 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z',
  },
  {
    text: 'Most frequent negative words all time',
    iconPath: 'M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z',
  },
  {
    text: 'Top complaint topics',
    iconPath: 'M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z',
  },
  {
    text: 'Percentage of mixed reviews in the last year',
    iconPath: 'M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z',
  },
];

interface UIMessage {
  role: 'human' | 'ai';
  content: string;
}

const BotAvatar = () => (
  <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center shrink-0">
    <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
    </svg>
  </div>
);

const UserAvatar = () => (
  <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center shrink-0">
    <svg className="w-4 h-4 text-gray-500" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
    </svg>
  </div>
);

const TypingDots = () => (
  <div className="flex items-end gap-2 mb-4">
    <BotAvatar />
    <div className="bg-white shadow-sm rounded-2xl rounded-bl-sm px-4 py-3">
      <span className="flex gap-1 items-center h-5">
        <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
        <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
        <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
      </span>
    </div>
  </div>
);

function normalizeMarkdown(text: string): string {
  return text
    .replace(/^[•·]\s*$/gm, '')    // strip lone bullet chars on their own line
    .replace(/^[•·]\s+/gm, '- ')   // "• text" → "- text"
    .replace(/^–\s*/gm, '- ')      // en-dash bullets → "- "
    .replace(/\n{3,}/g, '\n\n')    // collapse 3+ blank lines
    .trim();
}

const BotBubble: React.FC<{ content: string }> = ({ content }) => (
  <div className="flex items-end gap-2 mb-4 max-w-[85%]">
    <BotAvatar />
    <div className="bg-white shadow-sm rounded-2xl rounded-bl-sm px-4 py-3 text-base text-gray-700">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({ children }: any) => <p className="mb-1 last:mb-0">{children}</p>,
          strong: ({ children }: any) => <strong className="font-semibold text-gray-800">{children}</strong>,
          ul: ({ children }: any) => <ul className="list-disc list-outside ml-4 space-y-1 my-1">{children}</ul>,
          ol: ({ children }: any) => <ol className="list-decimal list-outside ml-4 space-y-1 my-1">{children}</ol>,
          li: ({ children }: any) => <li className="leading-relaxed">{children}</li>,
          h2: ({ children }: any) => <h2 className="font-semibold text-gray-800 mt-2 mb-1">{children}</h2>,
          h3: ({ children }: any) => <h3 className="font-semibold text-gray-700 mt-1.5 mb-0.5">{children}</h3>,
          code: ({ children }: any) => <code className="text-xs bg-gray-100 px-1 rounded font-mono">{children}</code>,
        }}
      >
        {normalizeMarkdown(content)}
      </ReactMarkdown>
    </div>
  </div>
);

const UserBubble: React.FC<{ content: string }> = ({ content }) => (
  <div className="flex items-end gap-2 mb-4 ml-auto max-w-[75%]">
    <div className="bg-blue-500 text-white rounded-2xl rounded-br-sm px-4 py-3 text-base">
      {content}
    </div>
    <UserAvatar />
  </div>
);

const ChatAssistant: React.FC<Props> = ({ sessionId, onSessionCreated, onSessionsChanged }) => {
  const [messages, setMessages] = useState<UIMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  // Prevents the sessionId-change effect from clearing messages when we just created a session
  const skipNextLoadRef = useRef(false);
  const chatHistoryApi = useChatHistoryApi();

  const scrollToBottom = useCallback(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  // Load messages when session changes (switching to an existing session)
  useEffect(() => {
    if (!sessionId) {
      setMessages([]);
      return;
    }
    // Skip when we just created this session during sendMessage — messages are already in state
    if (skipNextLoadRef.current) {
      skipNextLoadRef.current = false;
      return;
    }
    setLoadingHistory(true);
    setMessages([]);
    chatHistoryApi.getMessages(
      sessionId,
      (_msg: any, resp: any) => {
        // appApiCallHook passes (message, data) — resp is data.data (the array)
        const msgs: UIMessage[] = (Array.isArray(resp) ? resp : [])
          .filter((m: any) => m.role === 'human' || m.role === 'ai')
          .map((m: any) => ({ role: m.role, content: m.content }));
        setMessages(msgs);
        setLoadingHistory(false);
        setTimeout(scrollToBottom, 50);
      },
      () => setLoadingHistory(false)
    );
  }, [sessionId]);

  // Auto-scroll on new messages
  useEffect(() => {
    scrollToBottom();
  }, [messages, loading]);

  // Auto-resize textarea — let CSS control height when empty so padding centers the placeholder
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    if (!input) {
      ta.style.height = '';
    } else {
      ta.style.height = 'auto';
      ta.style.height = Math.min(ta.scrollHeight, 120) + 'px';
    }
  }, [input]);

  const sendMessage = async (text?: string) => {
    const question = (text ?? input).trim();
    if (!question || loading) return;

    setInput('');
    setMessages((prev) => [...prev, { role: 'human', content: question }]);
    setLoading(true);

    const doQuery = (sid: string) => {
      chatHistoryApi.sendQuery(
        sid,
        question,
        (_msg: any, resp: any) => {
          // appApiCallHook passes (message, data) — resp is data.data = {output, session_id}
          const output: string = resp?.output || '';
          setMessages((prev) => [...prev, { role: 'ai', content: output }]);
          setLoading(false);
          onSessionsChanged();
        },
        () => {
          setMessages((prev) => [...prev, { role: 'ai', content: 'Something went wrong. Please try again.' }]);
          setLoading(false);
        }
      );
    };

    if (sessionId) {
      doQuery(sessionId);
    } else {
      chatHistoryApi.createSession(
        (_msg: any, resp: any) => {
          // appApiCallHook passes (message, data) — resp is data.data = {session_id, title, created_at}
          const newId: string = resp?.session_id;
          if (newId) {
            // Skip the useEffect history reload — messages are already set in state
            skipNextLoadRef.current = true;
            onSessionCreated(newId);
            onSessionsChanged();
            doQuery(newId);
          }
        },
        () => {
          setMessages((prev) => [...prev, { role: 'ai', content: 'Could not create session. Please try again.' }]);
          setLoading(false);
        }
      );
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="flex flex-col h-full bg-sky-50">
      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-6 py-4 flex flex-col">
        {loadingHistory ? (
          <div className="flex justify-center items-center h-full">
            <div className="w-5 h-5 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : messages.length === 0 && !loading ? (
          /* Empty state */
          <div className="flex flex-col items-center justify-center h-full gap-7">
            <div className="w-14 h-14 bg-blue-500 rounded-2xl flex items-center justify-center shadow-md">
              <svg className="w-7 h-7 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
              </svg>
            </div>
            <p className="text-2xl font-semibold text-gray-700">How can I help you today?</p>
            <div className="grid grid-cols-2 gap-3 w-full max-w-2xl">
              {SUGGESTED_PROMPTS.map((p) => (
                <button
                  key={p.text}
                  onClick={() => sendMessage(p.text)}
                  className="flex flex-col gap-3 bg-white border border-gray-200 hover:border-blue-300 hover:bg-blue-50 rounded-2xl p-4 text-left transition-all group"
                >
                  <div className="w-8 h-8 bg-gray-100 group-hover:bg-blue-100 rounded-lg flex items-center justify-center text-gray-500 group-hover:text-blue-600 transition-colors">
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d={p.iconPath} />
                    </svg>
                  </div>
                  <span className="text-sm font-medium text-gray-700 leading-snug">{p.text}</span>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <>
            {messages.map((msg, i) =>
              msg.role === 'ai'
                ? <BotBubble key={i} content={msg.content} />
                : <UserBubble key={i} content={msg.content} />
            )}
            {loading && <TypingDots />}
          </>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input bar */}
      <div className="bg-white border-t border-gray-100 px-6 pb-4 pt-3 shrink-0">
        <div className="relative">
          <textarea
            ref={textareaRef}
            className="w-full rounded-2xl border border-gray-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none resize-none pl-4 pr-14 py-4 text-base text-gray-800 placeholder:text-gray-400 transition-all overflow-y-hidden"
            rows={1}
            placeholder="Ask about your customer feedback..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={loading}
          />
          <button
            onClick={() => sendMessage()}
            disabled={!input.trim() || loading}
            className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-100 disabled:cursor-not-allowed text-white disabled:text-gray-400 rounded-xl flex items-center justify-center transition-colors"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 10.5L12 3m0 0l7.5 7.5M12 3v18" />
            </svg>
          </button>
        </div>
        <p className="text-xs text-gray-400 mt-1.5 text-center">Press Enter to send · Shift+Enter for new line</p>
      </div>
    </div>
  );
};

export default ChatAssistant;
